import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FaSearch, FaMapMarkerAlt, FaCalendarAlt, FaBus, FaArrowRight,
  FaFilter, FaStar, FaUserFriends, FaSnowflake, FaWifi, FaPlug,
  FaFilm, FaArrowLeft, FaChevronDown, FaChevronUp, FaPhone,
  FaLocationArrow, FaBars, FaTimes, FaGlobe, FaCity,
  FaExclamationTriangle, FaSpinner, FaClock, FaMapPin, FaLanguage,
  FaCommentDots, FaSync, FaHome
} from 'react-icons/fa';
import { MdDateRange, MdAccessTime } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  fetchDistrictsByState,
  fetchCitiesByDistrict,
  searchBuses,
  setSearchFormData,
  clearSearchResults,
  setLanguage,
  selectSearchFormData,
  selectSearchStates,
  selectSearchDistricts,
  selectSearchCities,
  selectSearchResults,
  selectFullApiResponse,
  selectSearchAttempted,
  selectLoadingDistricts,
  selectLoadingCities,
  selectLoadingBuses,
  selectErrors,
  selectLanguage,
  resetSearchPage,
} from '../redux/Slices/busSlice';

import logo from '../../components/assets/logos/logo2.PNG';

const UserBusFinder = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const formData = useSelector(selectSearchFormData);
  const states = useSelector(selectSearchStates);
  const districts = useSelector(selectSearchDistricts);
  const cities = useSelector(selectSearchCities);
  const searchResults = useSelector(selectSearchResults);
  const fullApiResponse = useSelector(selectFullApiResponse);
  const searchAttempted = useSelector(selectSearchAttempted);
  const loading = {
    districts: useSelector(selectLoadingDistricts),
    cities: useSelector(selectLoadingCities),
    buses: useSelector(selectLoadingBuses)
  };
  const errors = useSelector(selectErrors);
  const currentLanguage = useSelector(selectLanguage);

  const [hoveredButton, setHoveredButton] = useState(null);
  const [hoveredLink, setHoveredLink] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [highlightDescriptions, setHighlightDescriptions] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [mpAutoSelected, setMpAutoSelected] = useState(false);

  const resultsRef = useRef(null);

  useEffect(() => {
    const checkScreenSize = () => setIsMobile(window.innerWidth <= 768);
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferred-language');
    if (savedLanguage && savedLanguage !== i18n.language) {
      i18n.changeLanguage(savedLanguage);
      dispatch(setLanguage(savedLanguage));
    }
  }, [i18n, dispatch]);

  // ============================================================
  // ✅ AUTO-SELECT MADHYA PRADESH (MP) STATE
  // ------------------------------------------------------------
  // Runs once when states are loaded. Finds MP by stateCode or
  // stateName, and sets it for both fromState and toState.
  // ============================================================
  useEffect(() => {
    if (mpAutoSelected || !states || states.length === 0) return;

    const mpState = states.find((s) => {
      const code = String(s.stateCode || '').toUpperCase();
      const name = String(s.stateName || s.name || '').toLowerCase();
      const hindi = String(s.hindi || '').toLowerCase();
      return (
        code === 'MP' ||
        code === '23' ||
        name.includes('madhya pradesh') ||
        name.includes('madhyapradesh') ||
        name === 'mp' ||
        hindi.includes('मध्य प्रदेश') ||
        hindi.includes('मध्यप्रदेश')
      );
    });

    if (mpState) {
      const mpCode = mpState.stateCode;
      // Only auto-select if the user hasn't already picked something
      if (!formData.fromState) {
        dispatch(setSearchFormData({ fromState: mpCode }));
        dispatch(fetchDistrictsByState({ stateCode: mpCode, type: 'from' }));
      }
      if (!formData.toState) {
        dispatch(setSearchFormData({ toState: mpCode }));
        dispatch(fetchDistrictsByState({ stateCode: mpCode, type: 'to' }));
      }
      setMpAutoSelected(true);
    }
  }, [states, mpAutoSelected, formData.fromState, formData.toState, dispatch]);

  useEffect(() => {
    if (searchResults.length > 0) {
      setHighlightDescriptions(true);
      const timer = setTimeout(() => setHighlightDescriptions(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [searchResults]);

  const handlereset = () => {
    dispatch(resetSearchPage());
    setMpAutoSelected(false); // allow re-auto-select after reset if needed
  };

  const scrollToResults = () => {
    if (resultsRef.current) {
      const headerHeight = isMobile ? 70 : 80;
      const elementPosition = resultsRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    dispatch(setLanguage(lng));
    localStorage.setItem('preferred-language', lng);
    document.documentElement.dir = (lng === 'ar' || lng === 'he') ? 'rtl' : 'ltr';
    if (formData.fromState) {
      dispatch(fetchDistrictsByState({ stateCode: formData.fromState, type: 'from' }));
    }
    if (formData.toState) {
      dispatch(fetchDistrictsByState({ stateCode: formData.toState, type: 'to' }));
    }
    if (formData.fromDistrict) {
      dispatch(fetchCitiesByDistrict({ districtCode: formData.fromDistrict, type: 'from' }));
    }
    if (formData.toDistrict) {
      dispatch(fetchCitiesByDistrict({ districtCode: formData.toDistrict, type: 'to' }));
    }
    if (searchResults.length > 0) {
      dispatch(searchBuses({ fromCity: formData.fromCity, toCity: formData.toCity }));
    }
  };

  const handleRefreshSelections = async () => {
    setIsRefreshing(true);
    dispatch(setSearchFormData({
      fromState: '',
      fromDistrict: '',
      fromCity: '',
      toState: '',
      toDistrict: '',
      toCity: '',
    }));
    dispatch(clearSearchResults());
    await new Promise(resolve => setTimeout(resolve, 300));
    setIsRefreshing(false);
    setMpAutoSelected(false); // allow MP re-selection after reset
    const searchCard = document.querySelector('[style*="searchCard"]');
    if (searchCard) {
      searchCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const getTranslatedText = (key, defaultValue = '') => {
    const translation = t(key);
    return translation !== key ? translation : (defaultValue || key);
  };

  const getTranslatedApiText = (item, fieldName) => {
    if (!item) return '';
    if (currentLanguage === 'hi' && item.hindi) return item.hindi;
    return item[fieldName] || item.name || item.displayName || '';
  };

  // ✅ SORT CITIES ALPHABETICALLY BY NAME
  const getFilteredCities = (type) => {
    let cityList;
    if (type === 'from') {
      cityList = cities.fromCities.filter(city => !formData.toCity || city.code !== formData.toCity);
    } else {
      cityList = cities.toCities.filter(city => !formData.fromCity || city.code !== formData.fromCity);
    }
    // Sort alphabetically by name (A–Z)
    return [...cityList].sort((a, b) => {
      const nameA = (currentLanguage === 'hi' && a.hindi) ? a.hindi : (a.name || '');
      const nameB = (currentLanguage === 'hi' && b.hindi) ? b.hindi : (b.name || '');
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    });
  };

  const getTranslatedOperator = (bus) => {
    if (!bus) return '';
    if (currentLanguage === 'hi') {
      if (bus.rawBusData) {
        if (bus.rawBusData.operatorHindi) return bus.rawBusData.operatorHindi;
        if (bus.rawBusData.hindiOperator) return bus.rawBusData.hindiOperator;
        if (bus.rawBusData.operator?.hindi) return bus.rawBusData.operator.hindi;
      }
      if (bus.hindiOperator) return bus.hindiOperator;
      if (bus.operatorHindi) return bus.operatorHindi;
      if (bus.operatorObj) {
        if (bus.operatorObj.hindi) return bus.operatorObj.hindi;
        if (bus.operatorObj.hindiName) return bus.operatorObj.hindiName;
      }
      if (bus.operator) {
        const operatorTranslations = {
          'Private': 'प्राइवेट', 'Government': 'सरकारी', 'Express': 'एक्सप्रेस',
          'Deluxe': 'डीलक्स', 'Transport': 'परिवहन', 'Corporation': 'निगम',
          'Roadways': 'रोडवेज', 'Bus Service': 'बस सेवा', 'Travels': 'ट्रैवल्स',
          'Motors': 'मोटर्स', 'Pvt Ltd': 'प्राइवेट लिमिटेड', 'Limited': 'लिमिटेड',
          'UPSRTC': 'यूपीएसआरटीसी', 'Road Transport': 'सड़क परिवहन', 'State Transport': 'राज्य परिवहन'
        };
        let translatedOperator = bus.operator;
        Object.keys(operatorTranslations).forEach(key => {
          const regex = new RegExp(key, 'gi');
          translatedOperator = translatedOperator.replace(regex, operatorTranslations[key]);
        });
        return translatedOperator;
      }
    }
    return bus.operator || bus.regNo || '';
  };

  const getTranslatedBusType = (bus) => {
    if (!bus) return '';
    if (bus.rawBusData && bus.rawBusData.bType !== undefined) {
      const bType = bus.rawBusData.bType;
      if (bType === 0) return currentLanguage === 'hi' ? 'साधारण' : 'Ordinary';
      if (bType === 1) return currentLanguage === 'hi' ? 'नॉन-स्टॉप' : 'NonStop';
    }
    const typeValue = bus.type || bus.busType || '';
    const typeLower = typeValue.toLowerCase();
    if (typeLower.includes('ordinary')) return currentLanguage === 'hi' ? 'साधारण' : 'Ordinary';
    else if (typeLower.includes('nonstop') || typeLower.includes('non stop')) return currentLanguage === 'hi' ? 'नॉन-स्टॉप' : 'NonStop';
    return '';
  };

  const formatDuration = (duration) => {
    if (!duration) return '';
    if (currentLanguage === 'hi') {
      return duration
        .replace(/hrs?/gi, 'घं.')
        .replace(/mins?/gi, 'मि.')
        .replace(/h/gi, 'घं.')
        .replace(/m/gi, 'मि.');
    }
    return duration.replace('hr', 'Hr.').replace('min', 'Min.');
  };

  const calculateDuration = (departureTime, arrivalTime, fallbackDuration) => {
    if (!departureTime || !arrivalTime) {
      return formatDuration(fallbackDuration);
    }

    try {
      const parseTime = (t) => {
        const parts = t.split(':');
        if (parts.length < 2) return null;
        return parseInt(parts[0]) * 60 + parseInt(parts[1]);
      };

      const depMinutes = parseTime(departureTime);
      const arrMinutes = parseTime(arrivalTime);

      if (depMinutes === null || arrMinutes === null) {
        return formatDuration(fallbackDuration);
      }

      let diffMinutes = arrMinutes - depMinutes;
      if (diffMinutes < 0) {
        diffMinutes += 24 * 60;
      }

      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;

      if (currentLanguage === 'hi') {
        if (hours > 0 && minutes > 0) return `${hours} घं. ${minutes} मि.`;
        if (hours > 0) return `${hours} घं.`;
        return `${minutes} मि.`;
      }

      if (hours > 0 && minutes > 0) return `${hours} h. ${minutes} m.`;
      if (hours > 0) return `${hours} h.`;
      return `${minutes} m.`;
    } catch (error) {
      return formatDuration(fallbackDuration);
    }
  };

  const getBusDescription = (bus) => {
    if (bus.rawBusData) {
      if (currentLanguage === 'hi' && bus.rawBusData.hindi) return bus.rawBusData.hindi;
      if (bus.rawBusData.description) return bus.rawBusData.description;
    }
    return bus.description || bus.routePath?.description || bus.routeDescription || null;
  };

  // ============================================================
  // ✅ FIXED: getBusTimingsFromArrival
  // ------------------------------------------------------------
  // This mirrors the logic used in BusDetails.jsx:
  //  1. Filter fullApiResponse.arrival by the specific bus number.
  //  2. Sort the filtered stops by sortId.
  //  3. First stop's arrivalTime  -> departure
  //  4. Last  stop's arrivalTime  -> arrival
  // ============================================================
  const getBusTimingsFromArrival = (bus) => {
    if (!bus) {
      return { departure: null, arrival: null };
    }

    // Resolve the bus identifier used for filtering.
    const busNo =
      bus?.rawBusData?.busNo ||
      bus?.busNo ||
      bus?.rawBusData?.busRegistrationNumber ||
      bus?.busNumber ||
      bus?.regNo ||
      null;

    let arrivalArray = null;

    // Preferred source: fullApiResponse.arrival (filtered by this bus)
    if (
      fullApiResponse &&
      Array.isArray(fullApiResponse.arrival) &&
      fullApiResponse.arrival.length > 0
    ) {
      if (busNo) {
        const filtered = fullApiResponse.arrival.filter(
          (t) =>
            t.busNo === busNo ||
            t.busRegistrationNumber === busNo ||
            t.regNo === busNo
        );
        if (filtered.length > 0) {
          arrivalArray = filtered;
        }
      }
    }

    // Fallback: per-bus rawBusData.arrival
    if (
      !arrivalArray &&
      bus.rawBusData &&
      Array.isArray(bus.rawBusData.arrival) &&
      bus.rawBusData.arrival.length > 0
    ) {
      arrivalArray = bus.rawBusData.arrival;
    }

    // Fallback: bus.arrival as an array
    if (!arrivalArray && Array.isArray(bus.arrival) && bus.arrival.length > 0) {
      arrivalArray = bus.arrival;
    }

    // If we still have no array, fall back to whatever string values exist
    if (!arrivalArray) {
      return {
        departure: bus.departure || null,
        arrival: typeof bus.arrival === 'string' ? bus.arrival : null,
      };
    }

    // Sort by sortId (same as BusDetails.jsx)
    const sorted = [...arrivalArray].sort(
      (a, b) => (a.sortId ?? 0) - (b.sortId ?? 0)
    );

    const firstStop = sorted[0];
    const lastStop = sorted[sorted.length - 1];

    const departure =
      firstStop?.arrivalTime ||
      firstStop?.departureTime ||
      bus.departure ||
      null;

    const arrival =
      lastStop?.arrivalTime ||
      lastStop?.departureTime ||
      (typeof bus.arrival === 'string' ? bus.arrival : null);

    return { departure, arrival };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'fromCity' && formData.toCity && value === formData.toCity) {
      alert(currentLanguage === 'hi' 
        ? 'प्रस्थान और गंतव्य शहर/ग्राम समान नहीं हो सकते। कृपया अलग-अलग स्थान चुनें।' 
        : 'Departure and destination cities/villages cannot be the same. Please choose different locations.');
      return;
    }

    if (name === 'toCity' && formData.fromCity && value === formData.fromCity) {
      alert(currentLanguage === 'hi' 
        ? 'प्रस्थान और गंतव्य शहर/ग्राम समान नहीं हो सकते। कृपया अलग-अलग स्थान चुनें।' 
        : 'Departure and destination cities/villages cannot be the same. Please choose different locations.');
      return;
    }

    dispatch(setSearchFormData({ [name]: value }));

    if (name === 'fromState' && value) {
      dispatch(fetchDistrictsByState({ stateCode: value, type: 'from' }));
    } else if (name === 'toState' && value) {
      dispatch(fetchDistrictsByState({ stateCode: value, type: 'to' }));
    } else if (name === 'fromDistrict' && value) {
      dispatch(fetchCitiesByDistrict({ districtCode: value, type: 'from' }));
    } else if (name === 'toDistrict' && value) {
      dispatch(fetchCitiesByDistrict({ districtCode: value, type: 'to' }));
    }

    if (name === 'fromState') {
      dispatch(setSearchFormData({ fromDistrict: '', fromCity: '' }));
    } else if (name === 'toState') {
      dispatch(setSearchFormData({ toDistrict: '', toCity: '' }));
    } else if (name === 'fromDistrict') {
      dispatch(setSearchFormData({ fromCity: '' }));
    } else if (name === 'toDistrict') {
      dispatch(setSearchFormData({ toCity: '' }));
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!formData.fromCity || !formData.toCity) {
      alert(getTranslatedText('selectBothLocations', 'Please select both departure and destination locations'));
      return;
    }

    if (formData.fromCity === formData.toCity) {
      alert(currentLanguage === 'hi' 
        ? 'प्रस्थान और गंतव्य शहर/ग्राम समान नहीं हो सकते। कृपया अलग-अलग स्थान चुनें।' 
        : 'Departure and destination cities/villages cannot be the same. Please choose different locations.');
      return;
    }

    dispatch(clearSearchResults());
    try {
      await dispatch(searchBuses({ fromCity: formData.fromCity, toCity: formData.toCity })).unwrap();
      setTimeout(() => scrollToResults(), 300);
    } catch (error) {
      console.error('Search failed:', error);
      setTimeout(() => scrollToResults(), 300);
    }
  };

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  const formatTimeForDisplay = (timeString) => {
    if (!timeString || timeString === 'N/A') return getTranslatedText('notAvailable', 'N/A');
    try {
      const timeParts = timeString.split(':');
      if (timeParts.length >= 2) {
        const hours = parseInt(timeParts[0]);
        const minutes = timeParts[1];
        const period = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        return `${displayHours}:${minutes} ${period}`;
      }
      return timeString;
    } catch (error) {
      return timeString;
    }
  };

  const navigateToBusDetails = (bus) => {
    navigate('/bus-details', { 
      state: { 
        bus: bus,
        rawBusData: bus.rawBusData,
        formData: formData,
        fullApiResponse: fullApiResponse
      } 
    });
  };

  const isSameLocationBus = (bus) => {
    if (bus.duration) {
      const durationStr = bus.duration.toLowerCase();
      if (durationStr.includes('0 hr') || durationStr.includes('0hr') || 
          durationStr.includes('0 min') || durationStr.includes('0min') ||
          durationStr === '0' || durationStr === '0h' || durationStr === '0m') return true;
    }
    if (bus.departure && bus.arrival && bus.departure === bus.arrival) return true;
    return false;
  };

  const getSelectStyle = (isDisabled = false) => {
    const baseSelect = {
      width: '100%',
      padding: isMobile ? '12px 12px 12px 40px' : '14px 14px 14px 45px',
      border: '2px solid #e0e0e0',
      borderRadius: '10px',
      fontSize: isMobile ? '0.95rem' : '1.05rem', // ✅ Slightly increased
      fontWeight: '500',
      transition: 'all 0.3s ease',
      outline: 'none',
      backgroundColor: 'white',
      minHeight: '48px',
      appearance: 'none',
      cursor: 'pointer',
      boxSizing: 'border-box',
      lineHeight: currentLanguage === 'hi' ? '1.6' : '1.4',
      fontFamily: "'Noto Serif Devanagari', serif",
    };
    if (isDisabled) {
      return { ...baseSelect, backgroundColor: '#f5f5f5', color: '#999', cursor: 'not-allowed' };
    }
    return baseSelect;
  };

  const baseStyles = {
    container: {
      minHeight: '100vh',
      background: 'white',
      fontFamily: "'Noto Serif Devanagari', serif",
      padding: isMobile ? '10px 5px' : '20px 10px',
      margin: 0,
      width: '100%',
      boxSizing: 'border-box',
      overflowX: 'hidden'
    },
    header: {
      background: 'linear-gradient(135deg, #FFF9C4 0%, #FFECB3 100%)',
      color: '#5D4037',
      padding: isMobile ? '15px 10px' : '20px 15px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      position: 'sticky',
      top: 0,
      zIndex: 100,
      width: '100%',
      boxSizing: 'border-box'
    },
    headerContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%'
    },
    logoContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '10px' : '15px'
    },
    logoLink: {
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '10px' : '15px',
      textDecoration: 'none',
      color: '#5D4037'
    },
    logoImage: {
      height: isMobile ? '40px' : '50px',
      width: 'auto',
      objectFit: 'contain',
      borderRadius: '8px'
    },
    homeButton: {
      background: 'transparent',
      border: '2px solid #5D4037',
      color: '#5D4037',
      fontSize: isMobile ? '15px' : '17px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      borderRadius: '25px',
      transition: 'all 0.3s ease',
      fontWeight: '600',
      textDecoration: 'none',
      fontFamily: "'Noto Serif Devanagari', serif"
    },
    navLinks: {
      display: isMobile ? (mobileMenuOpen ? 'flex' : 'none') : 'flex',
      gap: isMobile ? '15px' : '30px',
      alignItems: 'center',
      flexDirection: isMobile ? 'column' : 'row',
      position: isMobile ? 'absolute' : 'static',
      top: isMobile ? '100%' : 'auto',
      left: isMobile ? '0' : 'auto',
      right: isMobile ? '0' : 'auto',
      background: isMobile ? 'linear-gradient(135deg, #FFF9C4 0%, #FFECB3 100%)' : 'transparent',
      padding: isMobile ? '20px' : '0',
      boxShadow: isMobile ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none',
      zIndex: isMobile ? 99 : 'auto',
      width: isMobile ? '100%' : 'auto'
    },
    navLink: {
      color: '#5D4037',
      textDecoration: 'none',
      fontSize: isMobile ? '0.9rem' : '1rem',
      fontWeight: '500',
      padding: isMobile ? '10px 15px' : '8px 16px',
      borderRadius: '6px',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      width: isMobile ? '100%' : 'auto',
      justifyContent: isMobile ? 'center' : 'flex-start'
    },
    navLinkHover: {
      backgroundColor: 'rgba(93, 64, 55, 0.1)'
    },
    mobileMenuButton: {
      background: 'none',
      border: 'none',
      color: '#5D4037',
      fontSize: '1.5rem',
      cursor: 'pointer',
      display: isMobile ? 'block' : 'none',
      padding: '5px'
    },
    mainContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '15px 10px' : '30px 15px',
      width: '100%',
      boxSizing: 'border-box'
    },
    searchCard: {
      background: 'white',
      borderRadius: isMobile ? '15px' : '20px',
      padding: isMobile ? '20px 15px' : '30px 20px',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      marginBottom: isMobile ? '30px' : '40px',
      position: 'relative',
      width: '100%',
      boxSizing: 'border-box'
    },
    searchTitle: {
      fontSize: isMobile ? '1.45rem' : '1.85rem', // ✅ Slightly increased
      fontWeight: '600',
      color: '#333',
      marginBottom: isMobile ? '20px' : '25px',
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '8px' : '12px',
      flexWrap: 'wrap'
    },
    searchForm: {
      display: 'grid',
      gap: isMobile ? '15px' : '20px',
      width: '100%'
    },
    formSection: {
      marginBottom: isMobile ? '15px' : '20px',
      width: '100%'
    },
    sectionTitle: {
      fontSize: isMobile ? '1.2rem' : '1.3rem', // ✅ Slightly increased
      fontWeight: '600',
      color: '#333',
      marginBottom: '15px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      paddingBottom: '8px',
      borderBottom: '2px solid #FFB300'
    },
    locationRow: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
      gap: isMobile ? '12px' : '15px',
      marginBottom: '15px',
      width: '100%'
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      position: 'relative',
      width: '100%'
    },
    label: {
      fontSize: isMobile ? '1rem' : '1.05rem', // ✅ Slightly increased
      fontWeight: '600',
      color: '#555',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    inputContainer: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      width: '100%'
    },
    inputIcon: {
      position: 'absolute',
      left: '12px',
      color: '#FFB300',
      fontSize: isMobile ? '1.05rem' : '1.15rem', // ✅ Slightly increased
      zIndex: 2
    },
    selectDisabled: {
      backgroundColor: '#f5f5f5',
      color: '#999',
      cursor: 'not-allowed'
    },
    errorText: {
      color: '#ff4444',
      fontSize: '0.8rem', // ✅ Slightly increased
      marginTop: '4px',
      display: 'flex',
      alignItems: 'center',
      gap: '5px'
    },
    arrowRow: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      margin: isMobile ? '5px 0' : '10px 0',
      width: '100%'
    },
    arrowSymbol: {
      background: '#FFB300',
      color: '#5D4037',
      width: isMobile ? '40px' : '50px',
      height: isMobile ? '40px' : '50px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isMobile ? '1.2rem' : '1.5rem',
      fontWeight: 'bold',
      boxShadow: '0 4px 12px rgba(255, 179, 0, 0.3)',
      transform: isMobile ? 'rotate(90deg)' : 'rotate(0deg)'
    },
    searchButton: {
      background: 'linear-gradient(135deg, #FFF9C4 0%, #FFECB3 100%)',
      color: '#5D4037',
      border: 'none',
      padding: isMobile ? '16px 20px' : '16px 40px',
      borderRadius: '12px',
      fontSize: isMobile ? '1.05rem' : '1.15rem', // ✅ Slightly increased
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
      transition: 'all 0.3s ease',
      marginTop: '20px',
      boxShadow: '0 6px 20px rgba(255, 201, 71, 0.4)',
      width: '100%',
      alignSelf: 'center',
      minHeight: '48px',
      boxSizing: 'border-box'
    },
    searchButtonHover: {
      transform: 'translateY(-3px)',
      boxShadow: '0 10px 25px rgba(255, 201, 71, 0.5)'
    },
    searchButtonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed'
    },
    refreshButton: {
      background: 'linear-gradient(135deg, #FFE082 0%, #FFB300 100%)',
      color: '#5D4037',
      border: 'none',
      padding: isMobile ? '12px 16px' : '14px 20px',
      borderRadius: '12px',
      fontSize: isMobile ? '0.95rem' : '1.05rem', // ✅ Slightly increased
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(255, 179, 0, 0.3)',
      minHeight: '44px',
      boxSizing: 'border-box',
      border: '2px solid #FFE082',
      marginLeft: 'auto'
    },
    refreshButtonHover: {
      transform: 'scale(1.02)',
      boxShadow: '0 8px 25px rgba(255, 179, 0, 0.5)',
      borderColor: '#FFB300'
    },
    refreshButtonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed'
    },
    refreshButtonSpinner: {
      animation: 'spin 1s linear infinite'
    },
    searchTitleRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '10px',
      width: '100%'
    },
    resultsSection: {
      marginTop: isMobile ? '30px' : '40px',
      width: '100%'
    },
    resultsHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
      marginBottom: isMobile ? '20px' : '25px',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '15px' : '0',
      width: '100%'
    },
    resultsTitle: {
      fontSize: isMobile ? '1.35rem' : '1.55rem', // ✅ Slightly increased
      fontWeight: '600',
      color: '#333',
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '8px' : '10px'
    },
    busList: {
      display: 'flex',
      flexDirection: 'column',
      gap: isMobile ? '15px' : '20px',
      width: '100%'
    },
    busCard: {
      background: 'white',
      borderRadius: isMobile ? '12px' : '15px',
      padding: isMobile ? '20px 15px' : '25px 20px',
      boxShadow: '0 5px 20px rgba(0, 0, 0, 0.08)',
      transition: 'all 0.3s ease',
      border: '1px solid transparent',
      width: '100%',
      boxSizing: 'border-box',
      cursor: 'default',
      userSelect: 'text',
    },
    busCardHover: {
      transform: isMobile ? 'translateY(-2px)' : 'translateY(-5px)',
      boxShadow: '0 15px 35px rgba(0, 0, 0, 0.15)',
      borderColor: '#FFB300'
    },
    busHeader: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: isMobile ? '15px' : '20px',
      flexDirection: 'column',
      gap: isMobile ? '8px' : '10px',
      width: '100%',
      textAlign: 'center'
    },
    busInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: isMobile ? '4px' : '5px',
      width: '100%',
      alignItems: 'center'
    },
    busOperator: {
      fontSize: isMobile ? '1.1rem' : '1.2rem', // ✅ Slightly increased
      color: '#3E2723',
      textAlign: 'center',
      fontWeight: '700',
      fontFamily: "'Noto Serif Devanagari', serif",
      letterSpacing: '0.2px'
    },
    busDetails: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: isMobile ? '18px 12px' : '22px 40px',
      marginBottom: isMobile ? '15px' : '20px',
      width: '100%',
      maxWidth: isMobile ? '100%' : '600px',
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    detailItem: {
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '8px' : '10px',
      width: '100%',
      justifyContent: 'center',
      textAlign: 'center',
    },
    detailText: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
      gap: '4px',
    },
    detailLabel: {
      fontSize: isMobile ? '1rem' : '1.1rem', // ✅ Slightly increased
      color: '#3E2723',
      fontWeight: '700',
      fontFamily: "'Noto Serif Devanagari', serif",
    },
    detailValue: {
      fontSize: isMobile ? '1.05rem' : '1.15rem', // ✅ Slightly increased
      fontWeight: '600',
      color: '#333',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontFamily: "'Noto Serif Devanagari', serif",
    },
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '40px 0' : '60px 0',
      width: '100%'
    },
    spinner: {
      width: isMobile ? '40px' : '50px',
      height: isMobile ? '40px' : '50px',
      border: '5px solid #f3f3f3',
      borderTop: '5px solid #FFB300',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      marginBottom: isMobile ? '15px' : '20px'
    },
    apiSpinner: {
      animation: 'spin 1s linear infinite',
      marginRight: '8px'
    },
    noResults: {
      textAlign: 'center',
      padding: isMobile ? '40px 0' : '60px 0',
      color: '#666',
      width: '100%'
    },
    showDetailsButton: {
      background: 'linear-gradient(135deg, #FFF9C4 0%, #FFECB3 100%)',
      color: '#5D4037',
      border: 'none',
      padding: isMobile ? '12px 15px' : '12px 20px',
      borderRadius: '8px',
      fontSize: isMobile ? '1rem' : '1.05rem', // ✅ Slightly increased
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(255, 201, 71, 0.3)',
      minHeight: '44px',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      marginTop: '20px',
      boxSizing: 'border-box'
    },
    showDetailsButtonHover: {
      transform: 'translateY(-3px)',
      boxShadow: '0 8px 20px rgba(255, 201, 71, 0.5)'
    },
    inactiveBadge: {
      background: '#ffebee',
      color: '#c62828',
      padding: '8px',
      borderRadius: '5px',
      marginBottom: '10px',
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      fontSize: '0.95rem', // ✅ Slightly increased
      width: '100%'
    },
    footer: {
      background: 'linear-gradient(135deg, #FFF9C4 0%, #FFECB3 100%)',
      color: '#5D4037',
      padding: isMobile ? '15px 20px' : '20px 40px',
      textAlign: 'center',
      marginTop: '40px',
      width: '100%',
      boxSizing: 'border-box',
      fontFamily: "'Noto Serif Devanagari', serif",
      fontWeight: 'inherit',
      borderTop: '2px solid #FFB300'
    },
    footerText: {
      margin: '0 0 15px 0',
      opacity: '0.9',
      fontSize: isMobile ? '0.95rem' : '1.05rem', // ✅ Slightly increased
      color: '#5D4037'
    },
    footerLinks: {
      display: 'flex',
      justifyContent: 'center',
      flexWrap: 'wrap',
      gap: isMobile ? '20px' : '30px',
      marginBottom: '5px'
    },
    footerLink: {
      color: '#5D4037',
      textDecoration: 'none',
      opacity: '0.8',
      transition: 'opacity 0.3s ease, color 0.3s ease',
      fontSize: isMobile ? '0.95rem' : '1.05rem', // ✅ Slightly increased
      padding: '5px 0',
      borderBottom: '1px solid transparent',
    },
    footerLinkHover: {
      color: '#FFB300'
    },
  };

  useEffect(() => {
    const styleSheet = document.styleSheets[0];
    if (styleSheet) {
      styleSheet.insertRule(`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`, styleSheet.cssRules.length);
      styleSheet.insertRule(`@keyframes popIn { 0% { transform: scale(0.95) rotate(-0.5deg); opacity: 0.7; } 100% { transform: scale(1) rotate(-0.5deg); opacity: 1; } }`, styleSheet.cssRules.length);
    }
  }, []);

  const getBusCardStyle = () => baseStyles.busCard;
  const getNavLinkStyle = (linkName) => hoveredLink === linkName ? { ...baseStyles.navLink, ...baseStyles.navLinkHover } : baseStyles.navLink;
  const getSearchButtonStyle = () => {
    const baseStyle = baseStyles.searchButton;
    const disabledStyle = loading.buses || !formData.fromCity || !formData.toCity ? { ...baseStyle, ...baseStyles.searchButtonDisabled } : baseStyle;
    return hoveredButton === 'search' && !loading.buses && formData.fromCity && formData.toCity ? { ...disabledStyle, ...baseStyles.searchButtonHover } : disabledStyle;
  };
  const getRefreshButtonStyle = () => {
    const baseStyle = baseStyles.refreshButton;
    const disabledStyle = isRefreshing ? { ...baseStyle, ...baseStyles.refreshButtonDisabled } : baseStyle;
    return hoveredButton === 'refresh' && !isRefreshing ? { ...disabledStyle, ...baseStyles.refreshButtonHover } : disabledStyle;
  };
  const getFooterText = () => currentLanguage === 'hi' ? '© 2026 राजदूत एक्सप्लोरेशंस। सभी अधिकार सुरक्षित।' : '© 2026 Rajdoot Explorations. All rights reserved.';
  const getShowDetailsButtonStyle = (busId) => hoveredButton === `details-${busId}` ? { ...baseStyles.showDetailsButton, ...baseStyles.showDetailsButtonHover } : baseStyles.showDetailsButton;

  return (
    <div style={baseStyles.container}>
      <header style={baseStyles.header}>
        <div style={baseStyles.headerContent}>
          <div style={baseStyles.logoContainer}>
            <Link to="/" style={baseStyles.logoLink} onClick={handlereset}>
              <img src={logo} alt={getTranslatedText('logoAlt', 'Bus Management System Logo')} style={baseStyles.logoImage} />
            </Link>
          </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            {!isMobile && (
              <Link
                to="/"
                style={baseStyles.homeButton}
                onClick={handlereset}
              >
                <FaHome /> {currentLanguage === 'hi' ? 'होम' : 'Home'}
              </Link>
            )}
            <button style={baseStyles.mobileMenuButton} onClick={toggleMobileMenu}>
              {mobileMenuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>
        {isMobile && mobileMenuOpen && (
          <nav style={baseStyles.navLinks}>
            <Link
              to="/"
              style={baseStyles.footerLink}
              onClick={() => { handlereset(); setMobileMenuOpen(false); }}
            >
              <FaBus /> {currentLanguage === 'hi' ? 'होम' : 'Home'}
            </Link>
          </nav>
        )}
      </header>

      <main style={baseStyles.mainContent}>
        <div style={baseStyles.searchCard}>
          <div style={baseStyles.searchTitleRow}>
            <h2 style={baseStyles.searchTitle}><FaSearch /> {getTranslatedText('findBuses', 'Bus Finder')}</h2>
            <button 
              style={getRefreshButtonStyle()} 
              onClick={handleRefreshSelections}
              onMouseEnter={() => setHoveredButton('refresh')}
              onMouseLeave={() => setHoveredButton(null)}
              disabled={isRefreshing}
              title={currentLanguage === 'hi' ? 'सभी चयन रीसेट करें' : 'Reset all selections'}
            >
              {isRefreshing ? (
                <FaSpinner style={baseStyles.refreshButtonSpinner} />
              ) : (
                <FaSync />
              )}
              {isRefreshing 
                ? (currentLanguage === 'hi' ? 'रीसेट हो रहा...' : 'Resetting...') 
                : (currentLanguage === 'hi' ? 'रीसेट करें' : 'Reset')}
            </button>
          </div>
          <form onSubmit={handleSearch} style={baseStyles.searchForm}>
            {/* Departure Section */}
            <div style={baseStyles.formSection}>
              <h3 style={baseStyles.sectionTitle}><FaMapMarkerAlt /> {getTranslatedText('departure', 'Departure')}</h3>
              <div style={baseStyles.locationRow}>
                <div style={baseStyles.inputGroup}>
                  <label style={baseStyles.label}><FaGlobe /> {getTranslatedText('state', 'State')}</label>
                  <div style={baseStyles.inputContainer}>
                    <FaGlobe style={baseStyles.inputIcon} />
                    <select name="fromState" value={formData.fromState} onChange={handleInputChange} style={getSelectStyle(loading.states)} required disabled={loading.states}>
                      <option value="">{getTranslatedText('selectState', 'Select State')}</option>
                      {states.map(state => <option key={state.stateCode} value={state.stateCode}>{getTranslatedApiText(state, 'stateName')}</option>)}
                    </select>
                  </div>
                  {loading.states && <div style={baseStyles.errorText}><FaSpinner style={baseStyles.apiSpinner} /> {getTranslatedText('loadingStates', 'Loading states...')}</div>}
                  {errors.states && !loading.states && <div style={baseStyles.errorText}><FaExclamationTriangle /> {getTranslatedText('noStatesAvailable', 'No states available')}</div>}
                </div>
                <div style={baseStyles.inputGroup}>
                  <label style={baseStyles.label}><FaCity /> {getTranslatedText('district', 'District')}</label>
                  <div style={baseStyles.inputContainer}>
                    <FaCity style={baseStyles.inputIcon} />
                    <select name="fromDistrict" value={formData.fromDistrict} onChange={handleInputChange} style={getSelectStyle(!formData.fromState || loading.districts)} disabled={!formData.fromState || loading.districts} required>
                      <option value="">{getTranslatedText('selectDistrict', 'Select District')}</option>
                      {districts.fromDistricts.map(district => <option key={district.districtCode} value={district.districtCode}>{getTranslatedApiText(district, 'districtName')}</option>)}
                    </select>
                  </div>
                  {loading.districts && formData.fromState && <div style={baseStyles.errorText}><FaSpinner style={baseStyles.apiSpinner} /> {getTranslatedText('loadingDistricts', 'Loading districts...')}</div>}
                  {errors.districts && !loading.districts && formData.fromState && districts.fromDistricts.length === 0 && <div style={baseStyles.errorText}><FaExclamationTriangle /> {getTranslatedText('noDistrictsAvailable', 'No districts available')}</div>}
                </div>
                <div style={baseStyles.inputGroup}>
                  <label style={baseStyles.label}><FaBus /> {currentLanguage === 'hi' ? 'शहर/ग्राम' : 'City/Village'}</label>
                  <div style={baseStyles.inputContainer}>
                    <FaBus style={baseStyles.inputIcon} />
                    <select name="fromCity" value={formData.fromCity} onChange={handleInputChange} style={getSelectStyle(!formData.fromDistrict || loading.cities)} disabled={!formData.fromDistrict || loading.cities} required>
                      <option value="">{currentLanguage === 'hi' ? 'शहर/ग्राम चुनें' : 'Select City/Village'}</option>
                      {getFilteredCities('from').map(city => <option key={city.code} value={city.code}>{getTranslatedApiText(city, 'name')}</option>)}
                    </select>
                  </div>
                  {loading.cities && formData.fromDistrict && <div style={baseStyles.errorText}><FaSpinner style={baseStyles.apiSpinner} /> {getTranslatedText('loadingCities', 'Loading cities...')}</div>}
                  {errors.cities && !loading.cities && formData.fromDistrict && cities.fromCities.length === 0 && <div style={baseStyles.errorText}><FaExclamationTriangle /> {getTranslatedText('noCitiesAvailable', 'No cities available')}</div>}
                </div>
              </div>
            </div>
            {/* Destination Section */}
            <div style={baseStyles.formSection}>
              <h3 style={baseStyles.sectionTitle}><FaMapMarkerAlt /> {getTranslatedText('destination', 'Destination')}</h3>
              <div style={baseStyles.locationRow}>
                <div style={baseStyles.inputGroup}>
                  <label style={baseStyles.label}><FaGlobe /> {getTranslatedText('state', 'State')}</label>
                  <div style={baseStyles.inputContainer}>
                    <FaGlobe style={baseStyles.inputIcon} />
                    <select name="toState" value={formData.toState} onChange={handleInputChange} style={getSelectStyle(loading.states)} required disabled={loading.states}>
                      <option value="">{getTranslatedText('selectState', 'Select State')}</option>
                      {states.map(state => <option key={state.stateCode} value={state.stateCode}>{getTranslatedApiText(state, 'stateName')}</option>)}
                    </select>
                  </div>
                </div>
                <div style={baseStyles.inputGroup}>
                  <label style={baseStyles.label}><FaCity /> {getTranslatedText('district', 'District')}</label>
                  <div style={baseStyles.inputContainer}>
                    <FaCity style={baseStyles.inputIcon} />
                    <select name="toDistrict" value={formData.toDistrict} onChange={handleInputChange} style={getSelectStyle(!formData.toState || loading.districts)} disabled={!formData.toState || loading.districts} required>
                      <option value="">{getTranslatedText('selectDistrict', 'Select District')}</option>
                      {districts.toDistricts.map(district => <option key={district.districtCode} value={district.districtCode}>{getTranslatedApiText(district, 'districtName')}</option>)}
                    </select>
                  </div>
                  {loading.districts && formData.toState && <div style={baseStyles.errorText}><FaSpinner style={baseStyles.apiSpinner} /> {getTranslatedText('loadingDistricts', 'Loading districts...')}</div>}
                  {errors.districts && !loading.districts && formData.toState && districts.toDistricts.length === 0 && <div style={baseStyles.errorText}><FaExclamationTriangle /> {getTranslatedText('noDistrictsAvailable', 'No districts available')}</div>}
                </div>
                <div style={baseStyles.inputGroup}>
                  <label style={baseStyles.label}><FaBus /> {currentLanguage === 'hi' ? 'शहर/ग्राम' : 'City/Village'}</label>
                  <div style={baseStyles.inputContainer}>
                    <FaBus style={baseStyles.inputIcon} />
                    <select name="toCity" value={formData.toCity} onChange={handleInputChange} style={getSelectStyle(!formData.toDistrict || loading.cities)} disabled={!formData.toDistrict || loading.cities} required>
                      <option value="">{currentLanguage === 'hi' ? 'शहर/ग्राम चुनें' : 'Select City/Village'}</option>
                      {getFilteredCities('to').map(city => <option key={city.code} value={city.code}>{getTranslatedApiText(city, 'name')}</option>)}
                    </select>
                  </div>
                  {loading.cities && formData.toDistrict && <div style={baseStyles.errorText}><FaSpinner style={baseStyles.apiSpinner} /> {getTranslatedText('loadingCities', 'Loading cities...')}</div>}
                  {errors.cities && !loading.cities && formData.toDistrict && cities.toCities.length === 0 && <div style={baseStyles.errorText}><FaExclamationTriangle /> {getTranslatedText('noCitiesAvailable', 'No cities available')}</div>}
                </div>
              </div>
            </div>
            <button type="submit" style={getSearchButtonStyle()} onMouseEnter={() => setHoveredButton('search')} onMouseLeave={() => setHoveredButton(null)} disabled={loading.buses || !formData.fromCity || !formData.toCity}>
              {loading.buses ? <><FaSpinner style={baseStyles.apiSpinner} /> {getTranslatedText('searching', 'Searching...')}</> : <><FaSearch /> {currentLanguage === 'hi' ? 'बसें ढूंढें ↓' : 'Search Buses ↓'}</>}
            </button>
          </form>
        </div>

        <div ref={resultsRef} style={baseStyles.resultsSection}>
          {loading.buses ? (
            <div style={baseStyles.loading}>
              <div style={baseStyles.spinner}></div>
              <p>{getTranslatedText('searchingForBuses', 'Searching for buses...')}</p>
            </div>
          ) : searchAttempted ? (
            searchResults.length > 0 ? (
              (() => {
                const validBuses = searchResults.filter(bus => bus.isActive !== false && !isSameLocationBus(bus));
                if (validBuses.length === 0) {
                  return (
                    <div style={baseStyles.noResults}>
                      <h3>{currentLanguage === 'hi' ? 'कोई वैध बस नहीं' : 'No Valid Buses'}</h3>
                      <p>{currentLanguage === 'hi' ? 'इस मार्ग पर कोई वैध बस नहीं मिली। कृपया कोई अन्य मार्ग चुनें।' : 'No valid buses found on this route. Please try a different route.'}</p>
                    </div>
                  );
                }
                return (
                  <>
                    <div style={baseStyles.resultsHeader}>
                      <h3 style={baseStyles.resultsTitle}><FaBus /> {currentLanguage === 'hi' ? `उपलब्ध बसें (कुल - ${validBuses.length})` : `Available Buses (Total - ${validBuses.length})`}</h3>
                    </div>
                    <div style={baseStyles.busList}>
                      {validBuses
                        .sort((a, b) => { const descA = getBusDescription(a); const descB = getBusDescription(b); if (descA && !descB) return -1; if (!descA && descB) return 1; return 0; })
                        .map(bus => {
                          let operatorDisplay = getTranslatedOperator(bus);
                          if (bus.regNo) operatorDisplay += ` (${bus.regNo})`;
                          const { departure, arrival } = getBusTimingsFromArrival(bus);
                          return (
                            <div 
                              key={bus.id} 
                              style={getBusCardStyle()}
                            >
                              {!bus.isActive && <div style={baseStyles.inactiveBadge}><FaExclamationTriangle /> {getTranslatedText('busInactive', 'Bus Inactive')}</div>}
                              <div style={baseStyles.busHeader}>
                                <div style={baseStyles.busInfo}>
                                  <p style={baseStyles.busOperator}>{operatorDisplay}</p>
                                  {bus.busNumber && <p style={baseStyles.busOperator}>{bus.busNumber}</p>}
                                </div>
                              </div>
                              <div style={baseStyles.busDetails}>
                                <div style={{ ...baseStyles.detailItem, gridColumn: '1', gridRow: '1' }}>
                                  <div style={baseStyles.detailText}>
                                    <span style={baseStyles.detailLabel}>
                                      {currentLanguage === 'hi' ? 'प्रस्थान' : 'Departure'}
                                    </span>
                                    <span style={baseStyles.detailValue}>
                                      <span style={{ color: '#5D4037', fontSize: '1.05rem' }}>◉</span>
                                      {formatTimeForDisplay(departure)}
                                    </span>
                                  </div>
                                </div>

                                <div style={{ ...baseStyles.detailItem, gridColumn: '2', gridRow: '1' }}>
                                  <div style={baseStyles.detailText}>
                                    <span style={baseStyles.detailLabel}>
                                      {currentLanguage === 'hi' ? 'अवधि' : 'Duration'}
                                    </span>
                                    <span style={baseStyles.detailValue}>
                                      <span style={{ color: '#5D4037', fontSize: '1.05rem' }}>→</span>
                                      {calculateDuration(departure, arrival, bus.duration) || 'N/A'}
                                    </span>
                                  </div>
                                </div>

                                <div style={{ ...baseStyles.detailItem, gridColumn: '1', gridRow: '2' }}>
                                  <div style={baseStyles.detailText}>
                                    <span style={baseStyles.detailLabel}>
                                      {currentLanguage === 'hi' ? 'आगमन' : 'Arrival'}
                                    </span>
                                    <span style={baseStyles.detailValue}>
                                      <span style={{ color: '#5D4037', fontSize: '1.05rem' }}>◉</span>
                                      {formatTimeForDisplay(arrival)}
                                    </span>
                                  </div>
                                </div>

                                <div style={{ ...baseStyles.detailItem, gridColumn: '2', gridRow: '2' }}>
                                  <div style={baseStyles.detailText}>
                                    <span style={baseStyles.detailLabel}>
                                      {currentLanguage === 'hi' ? 'प्रकार' : 'Type'}
                                    </span>
                                    <span style={baseStyles.detailValue}>
                                      <span style={{ color: '#5D4037', fontSize: '1.05rem' }}>▦</span>
                                      {getTranslatedBusType(bus)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              {getBusDescription(bus) && (
                                <div style={{ display: "flex", justifyContent: "center", width: "100%", margin: "15px 0 10px 0" }}>
                                  <div style={{ backgroundColor: "#FFB300", color: "#5D4037", padding: "10px 20px", borderRadius: "30px", fontSize: "14px", fontWeight: "600", boxShadow: "0 4px 15px rgba(255, 179, 0, 0.3)", display: "inline-block", textAlign: "center", maxWidth: "80%", border: "2px solid #FFE082", position: "relative" }}>
                                    {getBusDescription(bus)}
                                    <div style={{ position: "absolute", top: "-8px", left: "50%", transform: "translateX(-50%) rotate(45deg)", width: "15px", height: "15px", backgroundColor: "#FFB300", borderLeft: "2px solid #FFE082", borderTop: "2px solid #FFE082", borderRadius: "4px 0 0 0" }} />
                                  </div>
                                </div>
                              )}
                              <button 
                                style={getShowDetailsButtonStyle(bus.id)} 
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  navigateToBusDetails(bus); 
                                }} 
                                onMouseEnter={() => setHoveredButton(`details-${bus.id}`)} 
                                onMouseLeave={() => setHoveredButton(null)}
                              >
                                <FaBus /> {currentLanguage === 'hi' ? 'बस की जानकारी देखें →' : 'View Bus Details →'}
                              </button>
                            </div>
                          );
                        })}
                    </div>
                  </>
                );
              })()
            ) : (
              <div style={baseStyles.noResults}>
                <h3>{getTranslatedText('noBusesFound', 'No Buses Found')}</h3>
                <p>{getTranslatedText('tryDifferentRoutes', 'Try different routes or dates')}</p>
              </div>
            )
          ) : null}
        </div>
      </main>

      <footer style={baseStyles.footer}>
        <p style={baseStyles.footerText}>{getFooterText()}</p>
        <div style={baseStyles.footerLinks}>
          <Link to="/privacy" style={baseStyles.footerLink} onMouseEnter={() => setHoveredLink('privacy-footer')} onMouseLeave={() => setHoveredLink(null)}>{currentLanguage === 'hi' ? 'गोपनीयता नीति' : 'Privacy Policy'}</Link>
          <Link to="/terms" style={baseStyles.footerLink} onMouseEnter={() => setHoveredLink('terms-footer')} onMouseLeave={() => setHoveredLink(null)}>{currentLanguage === 'hi' ? 'सेवा की शर्तें' : 'Terms of Service'}</Link>
          <Link to="/contact" style={baseStyles.footerLink} onMouseEnter={() => setHoveredLink('contact-footer')} onMouseLeave={() => setHoveredLink(null)}>{currentLanguage === 'hi' ? 'संपर्क करें' : 'Contact Us'}</Link>
        </div>
      </footer>
    </div>
  );
};

export default UserBusFinder;